import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { X } from "lucide-react";
import { API_BASE_URL } from "@/lib/apiBase";
import { getFullName } from "@/lib/userUtils";
import OrgMemberAvatar from "@/components/avatars/OrgMemberAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Who can post controls + custom allowlist picker (org members).
 */
export default function ChannelPostingPolicyFields({
  accessToken,
  orgId,
  postingPolicy,
  onPostingPolicyChange,
  selectedIds = [],
  onSelectedIdsChange,
  selectedUsers = [],
  enabled = true,
}) {
  const [query, setQuery] = useState("");
  const [orgUsers, setOrgUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || postingPolicy !== "custom" || !orgId || !accessToken) {
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/org/${orgId}/members`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!cancelled) {
          setOrgUsers(
            (res.data || [])
              .map((m) => {
                const u = m.user || m;
                if (!u?.user_id) return null;
                return {
                  ...u,
                  org_email: m.org_email ?? u.org_email ?? null,
                  title: m.title ?? u.title ?? null,
                };
              })
              .filter(Boolean)
          );
        }
      } catch (err) {
        console.error("Failed to load org members:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, postingPolicy, orgId, accessToken]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const selectedPeople = useMemo(() => {
    const byId = new Map();
    for (const u of selectedUsers) {
      if (u?.user_id) byId.set(u.user_id, u);
    }
    for (const u of orgUsers) {
      if (u?.user_id && selectedSet.has(u.user_id)) byId.set(u.user_id, u);
    }
    return selectedIds
      .map((id) => byId.get(id) || { user_id: id })
      .filter(Boolean);
  }, [selectedIds, selectedUsers, orgUsers, selectedSet]);

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orgUsers.filter((u) => {
      if (selectedSet.has(u.user_id)) return false;
      if (!q) return true;
      const name = getFullName(u).toLowerCase();
      return (
        name.includes(q) ||
        (u.org_email || "").toLowerCase().includes(q) ||
        (u.title || "").toLowerCase().includes(q)
      );
    });
  }, [orgUsers, selectedSet, query]);

  const addUser = (user) => {
    if (!user?.user_id || selectedSet.has(user.user_id)) return;
    onSelectedIdsChange?.([...selectedIds, user.user_id], [
      ...selectedPeople.filter((p) => p.user_id !== user.user_id),
      user,
    ]);
    setQuery("");
  };

  const removeUser = (userId) => {
    onSelectedIdsChange?.(
      selectedIds.filter((id) => id !== userId),
      selectedPeople.filter((p) => p.user_id !== userId)
    );
  };

  if (!enabled) return null;

  return (
    <div className="space-y-1.5">
      <Label>Who can post</Label>
      <div className="flex gap-2 flex-wrap">
        <Button
          type="button"
          size="sm"
          variant={postingPolicy === "open" ? "default" : "outline"}
          onClick={() => onPostingPolicyChange("open")}
        >
          Everyone who joins
        </Button>
        <Button
          type="button"
          size="sm"
          variant={postingPolicy === "team_only" ? "default" : "outline"}
          onClick={() => onPostingPolicyChange("team_only")}
        >
          Team members only
        </Button>
        <Button
          type="button"
          size="sm"
          variant={postingPolicy === "custom" ? "default" : "outline"}
          onClick={() => onPostingPolicyChange("custom")}
        >
          Custom
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        {postingPolicy === "team_only"
          ? "Joined non-members can read and react only."
          : postingPolicy === "custom"
            ? "Only the people you select can send messages. Others can still join, read, and react."
            : "Any participant can send messages."}
      </p>

      {postingPolicy === "custom" && (
        <div className="space-y-2 pt-1">
          {selectedPeople.length > 0 ? (
            <ul className="flex flex-wrap gap-1.5">
              {selectedPeople.map((u) => (
                <li
                  key={u.user_id}
                  className="inline-flex items-center gap-1.5 rounded-md border bg-muted/40 pl-1 pr-1.5 py-0.5 text-xs"
                >
                  <OrgMemberAvatar
                    user={u}
                    className="h-5 w-5"
                    fallbackClassName="text-[9px]"
                  />
                  <span className="max-w-[10rem] truncate">
                    {getFullName(u) || "Member"}
                  </span>
                  <button
                    type="button"
                    className="p-0.5 rounded hover:bg-muted"
                    aria-label={`Remove ${getFullName(u) || "member"}`}
                    onClick={() => removeUser(u.user_id)}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              Select at least one person who can post.
            </p>
          )}
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search people in this organization…"
            className="h-8 text-sm"
          />
          <ul className="max-h-36 overflow-y-auto divide-y border rounded-md">
            {loading ? (
              <li className="px-3 py-2 text-xs text-muted-foreground">
                Loading…
              </li>
            ) : candidates.length === 0 ? (
              <li className="px-3 py-2 text-xs text-muted-foreground">
                {query.trim()
                  ? "No matching people"
                  : "Everyone selected, or no members found"}
              </li>
            ) : (
              candidates.slice(0, 40).map((u) => (
                <li key={u.user_id}>
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/50"
                    onClick={() => addUser(u)}
                  >
                    <OrgMemberAvatar
                      user={u}
                      className="h-7 w-7"
                      fallbackClassName="text-[10px]"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">
                        {getFullName(u)}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {u.title || u.org_email || "Member"}
                      </span>
                    </span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
